<template>
  <div>
    <v-row v-if="noData">
      <v-col class="background text-center text-h4">
        {{ generateMessage('properties_360.readings.nodata') }}
      </v-col>
    </v-row>
    <template v-else>
      <v-row class="background ma-0 align-center">
        <v-col class="pa-0">
          <v-select
            v-model="addedTitle"
            dense
            outlined
            clearable
            :items="dataTitles"
            :label="generateMessage('properties_360.readings.courbeSup')"
          ></v-select>
        </v-col>
        <v-col class="pa-0">
          <v-switch
            class="my-auto float-right"
            v-model="isTableMode"
            :label="generateMessage('properties_360.readings.table')"
            hide-details
          />
        </v-col>
        <v-col class="pa-0">
          <v-switch
            class="my-auto float-right"
            v-model="cumulated"
            :label="generateMessage('properties_360.readings.cumulated')"
            hide-details
          />
        </v-col>
        <v-col cols="1" class="d-flex justify-end">
          <graphs-icon-caption />
        </v-col>
      </v-row>
      <v-row class="background" v-if="!isTableMode">
        <v-col class="caption">
          <span>
            <div>
              {{ generateMessage('properties_360.readings.dateSelected') }}
            </div>
            <div class="font-weight-bold">{{ dateOnPointX }}</div>
          </span>
        </v-col>
        <template v-for="(dataset, index) in chartData.datasets">
          <v-col
            :key="dataset.index"
            v-if="
              dataset.label.substring(0, 5) !== 'indic' &&
                dataset.label.substring(0, 7) !== 'Journal' &&
                dataset.label.substring(0, 13) !== 'Relèves ADICT'
            "
            class="caption"
          >
            <span>
              <div>{{ dataset.label }}</div>
              <div class="font-weight-bold">{{ dataOnPointX[index] }}</div>
            </span>
          </v-col>
        </template>
      </v-row>
    </template>
    <v-data-table
      v-if="isTableMode"
      fixed-header
      disable-pagination
      hide-default-footer
      height="50vh"
      sort-by="dt_evt"
      :sort-desc="true"
      :items="tableData"
      :item-class="(item) => item.color"
      :headers="tableHeader"
    >
      <template v-slot:item.dt_evt="{ item }">
        {{ new Date(item.dt_evt).toLocaleDateString() }}
      </template>
    </v-data-table>
    <custom-chart-renderer v-else :title="title">
      <template v-slot:refresh>
        <v-btn icon color="green" @click="resetChart">
          <v-icon>mdi-cached</v-icon>
        </v-btn>
      </template>
      <bar-chart
        ref="chart"
        :chartData="chartData"
        :options="options"
        @unzoomed="resetZ = false"
        :reset="resetZ"
        :autoRerender="false"
        :displayed="displayed"
      />
    </custom-chart-renderer>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import CustomChartRenderer from '@/components/graphs/CustomChartRenderer';
import GraphsIconCaption from '@/pages/actionplan/actions/GraphsIconCaption';
import BarChart from '@/components/graphs/BarChart';
import { generateMessage } from '@/utils/i18n';
import { resultList, showColor } from './ReadingsUtils';

export default {
  name: 'ReadingsResultsGeneral',
  components: {
    CustomChartRenderer,
    BarChart,
    GraphsIconCaption,
  },
  props: {
    dataIn: { type: Array, required: false },
    dataInCumulated: { type: Array, required: false },
    dataInLogbook: { type: Array, required: false },
    market: { type: String, required: false },
    addColumn: { type: Array, required: false, default: () => [] },
    baseTemperature: { type: Number, required: false },
    indexRefDates: { type: Array, required: false, default: () => [] },
    indexTargetDates: { type: Array, required: false, default: () => [] },
    displayed: { type: Boolean },
  },
  data() {
    return {
      chartDataExposed: [],
      baseOptions: {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            title(items) {
              return new Date(items[0].label).toLocaleDateString();
            },
            label(items, data) {
              if (data.datasets[items.datasetIndex].data[items.index].message) {
                return data.datasets[items.datasetIndex].data[items.index]
                  .message;
              }
              return `${data.datasets[items.datasetIndex].label} : ${
                items.yLabel
              }`;
            },
          },
        },
        onHover: this.onHover,
        plugins: {
          zoom: {
            enabled: false,
            pad: { enabled: true, mode: 'x' },
            zoom: { enabled: true, mode: 'x' },
            pan: { enabled: true, mode: 'x' },
          },
        },
      },
      addedTitle: null,
      cumulated: false,
      isTableMode: false,
      resetZ: false,
      dataOnPointX: [],
      tmpValueFirstDate: null,
      tmpValueSecondDate: null,
      dateOnPointX: null,
    };
  },
  methods: {
    generateMessage,
    resetChart() {
      this.resetZ = true;
    },
    rerender() {
      this.$refs.chart.renderChart(
        this.$refs.chart.chartData,
        this.$refs.chart.options
      );
    },
    showColor,
    onHover(evt, elementEvent) {
      if (elementEvent.length > 0) {
        this.dataOnPointX = this.chartData.datasets.map((el) => {
          const value = el.data[elementEvent[0]._index];
          if (value && value.dt_evt) {
            return '';
          }
          return el._short
            ? Math.trunc(value)
            : value && Number(value).toFixed(3);
        });
        if (this.chartData.labels[elementEvent[0]._index]) {
          this.dateOnPointX = this.chartData.labels[
            elementEvent[0]._index
          ].toLocaleDateString();
        }
      }
    },
    getColor(indexDate) {
      if (
        indexDate > this.indexRefDates[0] &&
        indexDate <= this.indexRefDates[1]
      ) {
        if (
          indexDate > this.indexTargetDates[0] &&
          indexDate <= this.indexTargetDates[1]
        ) {
          return 'warning--text';
        }
        return 'success--text';
      }
      if (
        indexDate > this.indexTargetDates[0] &&
        indexDate <= this.indexTargetDates[1]
      ) {
        return 'error--text';
      }
      return null;
    },
  },
  computed: {
    gridColor() {
      return this.$vuetify.theme.isDark ? '#3E414A' : '#DCDCDC';
    },
    options() {
      const opts = {
        ...this.baseOptions,
        legend: {
          position: 'bottom',
          labels: {
            fontColor: this.showColor('main'),
            filter(legendItem) {
              return (
                legendItem.text !== 'indic' &&
                legendItem.text !== 'indicBis' &&
                legendItem.text !== 'indicBorder' &&
                legendItem.text !== 'indicBorderBis'
              );
            },
          },
        },
        scales: {
          xAxes: [
            {
              distribution: 'linear',
              type: 'time',
              time: {
                unit: 'month',
                displayFormats: { month: 'MM-YYYY' },
              },
              ticks: { fontColor: this.showColor('main') },
              gridLines: { display: false },
            },
          ],
          yAxes: [
            {
              id: 'master',
              ticks: { beginAtZero: true, fontColor: this.showColor('main') },
              scaleLabel: {
                display: true,
                labelString: 'MWh (PCI)',
                fontColor: this.showColor('main'),
              },
              gridLines: { display: false },
            },
            {
              id: 'degreedays',
              ticks: { beginAtZero: true, fontColor: this.showColor('main') },
              scaleLabel: {
                display: true,
                labelString: 'degrés jours (°C)',
                fontColor: this.showColor('main'),
              },
              gridLines: { display: false },
            },
            {
              id: 'yRight',
              position: 'right',
              ticks: { beginAtZero: true, fontColor: this.showColor('main') },
              scaleLabel: {
                display: true,
                labelString: 'NC/DJ',
                fontColor: this.showColor('main'),
              },
              gridLines: { display: true, color: this.gridColor },
            },
            {
              id: 'yRight2',
              position: 'right',
              ticks: { beginAtZero: true, fontColor: this.showColor('main') },
              scaleLabel: { display: false },
              gridLines: { display: true, color: this.gridColor },
              display: 'auto',
            },
            {
              id: 'yRight3',
              position: 'right',
              ticks: { beginAtZero: true, fontColor: this.showColor('main') },
              scaleLabel: { display: false },
              gridLines: { display: true, color: this.gridColor },
              display: false,
            },
            {
              id: 'indic',
              position: 'right',
              display: false,
              ticks: { min: 0, max: 1, fontColor: this.showColor('main') },
              scaleLabel: { display: false },
            },
          ],
        },
      };
      return opts;
    },
    dataTitles() {
      return this.dataIn.length ? resultList : [];
    },
    title() {
      return `${
        this.market === 'F'
          ? this.generateMessage('properties_360.readings.consommation')
          : this.generateMessage('properties_360.readings.production')
      } ${this.generateMessage('properties_360.readings.titleMid')} ${
        this.cumulated
          ? this.generateMessage('properties_360.readings.titleEnd')
          : ''
      }`;
    },
    noData() {
      return !(this.dataIn && this.dataIn[0]);
    },
    chartData() {
      // Transform data for visualization
      const items = this.chartDataExposed;
      const firstDate =
        this.indexRefDates[0] || this.indexRefDates[0] === 0
          ? this.indexRefDates[0]
          : items.length;
      const secondDate = this.indexRefDates[1] || -1;
      const firstDateBis = this.indexTargetDates[0] || items.length;
      const secondDateBis = this.indexTargetDates[1] || -1;
      const labels = items.map((el) => new Date(el.dt_evt));
      items.forEach((element, index) => {
        if (element.nbjour > 0) {
          items[index].t_moy =
            this.baseTemperature -
            (element.dj_reel / element.nbjour).toFixed(1);
        }
      });

      const datasets = [
        {
          type: 'line',
          data: items.map((item, index) =>
            index >= firstDate && index <= secondDate ? 1 : null
          ),
          borderColor: 'rgba(0, 0, 0, 0)',
          backgroundColor: 'rgba(29,233,182,0.2)',
          pointRadius: 0,
          label: 'indic',
          yAxisID: 'indic',
        },
      ];

      datasets.push({
        type: 'line',
        data: items.map((item, index) =>
          index >= firstDateBis && index <= secondDateBis ? 1 : null
        ),
        borderColor: 'rgba(0, 0, 0, 0)',
        backgroundColor: 'rgba(255,192,203,0.3)',
        pointRadius: 0,
        label: 'indicBis',
        yAxisID: 'indic',
      });

      if (this.market === 'F') {
        datasets.push({
          type: 'line',
          label: this.generateMessage(
            'properties_360.readings.dataList.conso_tot'
          ),
          data: items.map((item) => item.conso_tot),
          borderColor: this.showColor('info'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'master',
        });

        datasets.push({
          type: 'line',
          label: this.generateMessage(
            'properties_360.readings.dataList.conso_tot_obj'
          ),
          data: items.map((item) => item.conso_tot_obj),
          borderColor: this.showColor('success'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'master',
          tickWidth: 0,
        });
      } else if (this.market === 'MC' || this.market === 'MCI') {
        datasets.push({
          type: 'line',
          label: this.generateMessage(
            'properties_360.readings.dataList.prod_mwh'
          ),
          data: items.map((item) => item.prod_mwh),
          borderColor: this.showColor('info'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'master',
        });

        datasets.push({
          type: 'line',
          label: this.generateMessage('properties_360.readings.dataList.cs'),
          data: items.map((item) => item.cs),
          borderColor: this.showColor('success'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yRight',
        });

        datasets.push({
          type: 'line',
          label: this.generateMessage(
            'properties_360.readings.dataList.cs_theo'
          ),
          data: items.map((item) => item.cs_theo),
          borderColor: this.showColor('purple'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yRight',
          tickWidth: 0,
        });
      }

      datasets.push({
        data: items.map((item, index) =>
          index === firstDate || index === secondDate ? 1 : null
        ),
        backgroundColor: 'rgb(29,233,182)',
        pointRadius: 0,
        label: 'indicBorder',
        yAxisID: 'indic',
      });

      datasets.push({
        data: items.map((item, index) =>
          index === firstDateBis || index === secondDateBis ? 1 : null
        ),
        backgroundColor: 'rgb(255,192,203)',
        pointRadius: 0,
        label: 'indicBorderBis',
        yAxisID: 'indic',
      });

      datasets.push({
        label: this.generateMessage('properties_360.readings.dataList.dj_reel'),
        data: items.map((item) => item.dj_reel),
        backgroundColor: this.showColor('secondary'),
        borderWidth: 2,
        barPercentage: 2,
        yAxisID: 'degreedays',
      });

      if (this.addedTitle) {
        const labelY = resultList.find((el) => el.value === this.addedTitle)
          .text;
        datasets.push({
          type: 'line',
          label: labelY,
          data: items.map((item) => item[this.addedTitle]),
          borderColor: this.showColor('warning'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yRight2',
          tickWidth: 0,
        });
      }
      if (this.dataInLogbook.length) {
        datasets.push({
          type: 'line',
          label: this.generateMessage('properties_360.readings.journalSte'),
          data: this.dataInLogbook
            .filter((el) => el.origin_id === 4)
            .map((el) => {
              return {
                x: new Date(el.dt_evt),
                y: 1,
                dt_evt: el.dt_evt,
                message: `${el.payload_type_description} : ${el.resume}`,
              };
            }),
          _short: true,
          borderColor: this.showColor('main'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          pointStyle: 'circle',
          pointRadius: 4,
          showLine: false,
          pointBorderWidth: 2,
          yAxisID: 'yRight3',
        });
        datasets.push({
          type: 'line',
          label: this.generateMessage('properties_360.readings.journalTra'),
          data: this.dataInLogbook
            .filter((el) => el.origin_id === 5)
            .map((el) => {
              return {
                x: new Date(el.dt_evt),
                y: 1,
                dt_evt: el.dt_evt,
                message: `${el.payload_type_description} : ${el.resume}`,
              };
            }),
          _short: true,
          borderColor: this.showColor('error'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          pointStyle: 'star',
          pointRadius: 4,
          showLine: false,
          pointBorderWidth: 1,
          yAxisID: 'yRight3',
        });
        datasets.push({
          type: 'line',
          label: this.generateMessage('properties_360.readings.journalAction'),
          data: this.dataInLogbook
            .filter((el) => el.origin_id === 7)
            .map((el) => {
              return {
                x: new Date(el.dt_evt),
                y: 1,
                dt_evt: el.dt_evt,
                message: `${el.payload_type_description} : ${el.resume}`,
              };
            }),
          _short: true,
          borderColor: this.showColor('success'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          pointStyle: 'triangle',
          pointRadius: 4,
          showLine: false,
          pointBorderWidth: 1,
          yAxisID: 'yRight3',
        });
        datasets.push({
          type: 'line',
          label: this.generateMessage('properties_360.readings.adict'),
          data: items
            .filter((el) => el.origin === 'GRDF')
            .map((el) => {
              return {
                x: new Date(el.dt_evt),
                y: 1,
                dt_evt: el.dt_evt,
                message: 'ADICT',
              };
            }),
          _short: true,
          borderColor: this.showColor('accent'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          pointStyle: 'rect',
          pointRadius: 4,
          showLine: false,
          pointBorderWidth: 1,
          yAxisID: 'yRight3',
        });
      }
      return { labels, items, datasets };
    },
    defaultHeaders() {
      const res = ['dt_evt', 'nbjour_chauffe'];
      if (this.market === 'F') {
        res.push('conso_tot');
        res.push('conso_tot_obj');
      } else if (this.market === 'MC' || this.market === 'MCI') {
        res.push('prod_mwh');
        res.push('cs');
      }
      if (this.addedTitle) {
        res.push(this.addedTitle);
      }
      return res.concat(this.addColumn);
    },
    tableHeader() {
      return this.defaultHeaders
        .filter(
          (item, index) =>
            index === this.defaultHeaders.findIndex((el) => el === item)
        )
        .map((el) => resultList.find((item) => item.value === el));
    },
    tableData() {
      return this.chartDataExposed.map((item, index) => {
        const newItem = { ...item };
        this.tableHeader.slice(1).forEach((el) => {
          if (newItem[el.value]) {
            newItem[el.value] = Math.trunc(newItem[el.value] * 1000) / 1000;
          }
        });
        newItem.color = this.getColor(index);
        return newItem;
      });
    },
  },
  watch: {
    dataIn: {
      handler(val) {
        this.chartDataExposed = val;
      },
      immediate: true,
    },
    cumulated(val) {
      this.chartDataExposed = val ? this.dataInCumulated : this.dataIn;
    },
    addedTitle(val) {
      if (val) {
        const labelY = resultList.find((el) => el.value === val).text;
        this.options.scales.yAxes[3].scaleLabel.labelString = labelY;
      }
    },
  },
  beforeMount() {
    if (this.market !== 'F') {
      this.options.scales.yAxes[2].scaleLabel.labelString = 'Cs';
    }
  },
};
</script>
